import { createSelector } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SECURE911 } from '@rsos/constants/spatialLayers';
import flattenArray from '@rsos/utils/flattenArray';
import useHasAccessToSecure911 from './useHasAccessToSecure911';
import useHasOnsiteNotificationAdr from './useHasOnsiteNotificationAdr';

/**
 * Memoized selector to retrieve the spatial objects from the Secure911
 * static layers.
 * NOTE: It's possible for there to be multiple layers with the same `source`.
 *   Find any matching layer's corresponding spatial objects containing the
 *   intersecting caller ID
 */
const selectSecure911SpatialObjects = createSelector(
  [state => state.spatialLayers.staticLayers],
  staticLayers => {
    const foundLayers = staticLayers.filter(layer => {
      return layer.source === SECURE911;
    });
    const spatialObjects = flattenArray(
      foundLayers.map(layer => {
        return layer.spatial_objects;
      }),
    );
    return spatialObjects;
  },
);

/**
 * Custom hook that returns a Boolean value for if the user has access to
 * the onsite notifications feature and there is data. This hook is intended to
 * be used to render the queue indicator and call pane banner. It does not
 * account for spatial layers data being persisted on page refresh
 * @param {String} callerId - caller ID
 */
const useShouldRenderOnsiteNotifications = callerId => {
  const hasAccessToSecure911 = useHasAccessToSecure911();

  const hasOnsiteNotificationAdr = useHasOnsiteNotificationAdr(callerId);

  const hasAccess = useMemo(() => {
    return [hasAccessToSecure911].some(access => !!access);
  }, [hasAccessToSecure911]);

  const secure911SpatialObjects = useSelector(selectSecure911SpatialObjects);

  // Any conditions for rendering the component
  const shouldRender = useMemo(() => {
    return !!(
      hasAccess &&
      (secure911SpatialObjects?.some(so =>
        so?.intersectingCalls?.includes(callerId),
      ) ||
        hasOnsiteNotificationAdr)
    );
  }, [hasAccess, secure911SpatialObjects, hasOnsiteNotificationAdr, callerId]);

  return shouldRender;
};

export default useShouldRenderOnsiteNotifications;
